import '../../styles/main.scss';
import '../../styles/pages/merchants.scss';
import { i18n } from '../../../i18n/i18n.js';
import { componentLoader } from '../components/loadComponents.js';
import { setupNavigation } from '../utils/navMenu.js';
import { initAccordion } from '../components/accordion.js';
import { sectionReveal } from '../utils/scrollReveal.js';
import { initMerchantsROICalculator } from '../components/merchantsROICalculator.js';

// 确保全局可以访问i18n
window.i18n = i18n;

/**
 * 初始化 Promo Tabs 切换功能
 * - 移动端：点击 + 触摸滑动
 * - PC 端：自动轮播 + 点击切换（点击后停止自动轮播）
 */
function initPromoTabs() {
  const section = document.querySelector('.merchants-promo');
  const tabs = document.querySelectorAll('.merchants-promo__tab');
  const track = document.querySelector('.merchants-promo__track');
  const panels = document.querySelector('.merchants-promo__panels');
  const tabsWrapper = document.querySelector('.merchants-promo__tabs-wrapper');

  if (!tabs.length || !track || !panels || !tabsWrapper || !section) return;

  const panelElements = document.querySelectorAll('.merchants-promo__panel');
  
  let currentIndex = 0;
  const totalSlides = tabs.length;
  const AUTOPLAY_DURATION = 6000; // 6 秒自动切换
  
  let isAutoPlaying = false;
  let autoPlayStopped = false; // 用户点击后永久停止
  let autoPlayTimer = null;

  // 检测是否是 PC 端
  function isDesktop() {
    return window.innerWidth >= 769;
  }

  // 切换到指定索引
  function goToSlide(index, isAuto = false) {
    // 循环处理
    if (index >= totalSlides) index = 0;
    if (index < 0) index = totalSlides - 1;
    
    currentIndex = index;

    // 更新 tab 状态
    tabs.forEach((t, i) => {
      const isActive = i === index;
      t.classList.toggle('is-active', isActive);
      t.setAttribute('aria-selected', isActive ? 'true' : 'false');
      
      // PC 端：更新播放/停止状态
      if (isDesktop()) {
        t.classList.remove('is-playing', 'is-stopped');
        if (isActive) {
          if (isAutoPlaying && !autoPlayStopped) {
            t.classList.add('is-playing');
          } else if (autoPlayStopped) {
            t.classList.add('is-stopped');
          }
        }
      }
    });

    // PC 端：渐隐渐出切换
    if (isDesktop()) {
      panelElements.forEach((p, i) => {
        p.classList.toggle('is-active', i === index);
      });
    } else {
      // 移动端：滑动切换
      track.style.transform = `translateX(-${index * 100}%)`;
    }

    // 移动端：滚动 tab 到可见区域
    if (!isDesktop()) {
      const activeTab = tabs[index];
      activeTab.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  }

  // 开始自动播放
  function startAutoPlay() {
    if (autoPlayStopped || !isDesktop()) return;
    
    isAutoPlaying = true;
    goToSlide(currentIndex, true);
    
    // 设置定时器切换到下一个
    autoPlayTimer = setTimeout(() => {
      goToSlide(currentIndex + 1, true);
      startAutoPlay(); // 递归继续
    }, AUTOPLAY_DURATION);
  }

  // 停止自动播放
  function stopAutoPlay(permanent = false) {
    isAutoPlaying = false;
    if (autoPlayTimer) {
      clearTimeout(autoPlayTimer);
      autoPlayTimer = null;
    }
    if (permanent) {
      autoPlayStopped = true;
      // 更新当前 tab 为停止状态
      tabs[currentIndex].classList.remove('is-playing');
      tabs[currentIndex].classList.add('is-stopped');
    }
  }

  // Tab 点击事件
  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => {
      // PC 端点击：停止自动播放
      if (isDesktop() && isAutoPlaying) {
        stopAutoPlay(true);
      }
      goToSlide(index);
      
      // 如果已停止自动播放，确保显示满进度条
      if (autoPlayStopped && isDesktop()) {
        tabs[index].classList.add('is-stopped');
      }
    });
  });

  // PC 端：进入视野后开始自动播放
  if (isDesktop()) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !autoPlayStopped) {
          startAutoPlay();
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.3 });
    
    observer.observe(section);
  }

  // 移动端：触摸滑动支持
  let startX = 0;
  let startY = 0;
  let isDragging = false;

  panels.addEventListener('touchstart', (e) => {
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
    isDragging = true;
  }, { passive: true });

  panels.addEventListener('touchmove', (e) => {
    if (!isDragging) return;
    
    const diffX = e.touches[0].clientX - startX;
    const diffY = e.touches[0].clientY - startY;
    
    if (Math.abs(diffY) > Math.abs(diffX)) {
      isDragging = false;
    }
  }, { passive: true });

  panels.addEventListener('touchend', (e) => {
    if (!isDragging) return;
    isDragging = false;

    const endX = e.changedTouches[0].clientX;
    const diffX = endX - startX;
    const threshold = 50;

    if (diffX > threshold) {
      goToSlide(currentIndex - 1);
    } else if (diffX < -threshold) {
      goToSlide(currentIndex + 1);
    }
  }, { passive: true });

  // 窗口大小变化时重新检测
  window.addEventListener('resize', () => {
    if (!isDesktop()) {
      stopAutoPlay();
    }
  });
}

// DOMContentLoaded 事件处理
document.addEventListener('DOMContentLoaded', async () => {
  // 加载页面组件
  await componentLoader.loadAllComponents();
  
  // 初始化多语言功能
  await i18n.init();
  
  // 设置导航菜单与高亮
  setupNavigation();
  
  // 初始化手风琴组件
  initAccordion('.accordion', {
    multiple: false,
    defaultOpen: 0, // 默认展开第一项
  });
  
  // 初始化滚动显现动画
  const sections = document.querySelectorAll('section');
  sectionReveal.reveal(Array.from(sections));

  // 初始化 ROI 计算器
  initMerchantsROICalculator();

  // 初始化 Promo Tabs
  initPromoTabs();
});